import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ReferenceImage } from '../../../services/imageGenerationApi';
import { LibraryItem } from '../../../types';
import LibraryImageModal from '../../LibraryImageModal';
import styles from './ReferenceImageUploader.module.css';

interface ReferenceImageUploaderProps {
  referenceImages: ReferenceImage[];
  onAddReferenceImage: (image: ReferenceImage) => void;
  onAddFromLibrary: (item: LibraryItem) => void;
  onRemoveReferenceImage: (index: number) => void;
  canAddMore: boolean;
  maxImages: number;
  projectId?: string;
  className?: string;
}

export type { ReferenceImageUploaderProps };

const ReferenceImageUploader: React.FC<ReferenceImageUploaderProps> = ({
  referenceImages,
  onAddReferenceImage,
  onAddFromLibrary,
  onRemoveReferenceImage,
  canAddMore,
  maxImages,
  projectId = '',
  className = ''
}) => {
  const { t } = useTranslation('aiGenerator');
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !canAddMore) return;

    Array.from(files).forEach((file, index) => {
      if (referenceImages.length + index >= maxImages) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          onAddReferenceImage({
            uri: result,
            tag: `ref_${referenceImages.length + index + 1}`,
            url: result,
            name: file.name,
            uploadedAt: new Date().toISOString()
          });
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    event.target.value = '';
  };  const handleLibrarySelection = (item: LibraryItem) => {
    onAddFromLibrary(item);
    setIsLibraryModalOpen(false);
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <label className={styles.label}>
        📷 {t('image.referenceImages', { count: referenceImages.length, max: maxImages })}:
      </label>
      
      <div className={styles.uploadArea}>
        {referenceImages.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>
              {t('image.uploadReferenceDesc')}
            </p>
            <div className={styles.buttonGroup}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="reference-upload"
                disabled={!canAddMore}
              />
              <label 
                htmlFor="reference-upload" 
                className={`${styles.uploadButton} ${!canAddMore ? styles.disabled : ''}`}
              >
                <FontAwesomeIcon icon={faPlus} />
                {t('image.chooseFiles')}
              </label>
              
              {projectId && (
                <button
                  onClick={() => setIsLibraryModalOpen(true)}
                  className={`${styles.libraryButton} ${!canAddMore ? styles.disabled : ''}`}
                  disabled={!canAddMore}
                >
                  <FontAwesomeIcon icon={faImage} />
                  {t('image.loadFromLibrary')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.imageGrid}>
            {referenceImages.map((img, index) => (
              <div key={index} className={styles.imageItem}>
                <img 
                  src={img.uri} 
                  alt={t('image.referenceAlt', { number: index + 1 })} 
                  className={styles.image}
                />
                <button
                  onClick={() => onRemoveReferenceImage(index)}
                  className={styles.removeButton}
                  title={t('image.removeImage')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
            
            {canAddMore && (
              <div className={styles.addMoreContainer}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="reference-upload-more"
                />
                <label htmlFor="reference-upload-more" className={styles.addMoreButton}>
                  <FontAwesomeIcon icon={faPlus} />
                </label>
                
                {projectId && (
                  <button
                    onClick={() => setIsLibraryModalOpen(true)}
                    className={styles.addMoreLibraryButton}
                    title={t('image.addFromLibrary')}
                  >
                    <FontAwesomeIcon icon={faImage} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Library Selection Modal */}
      {projectId && (
        <LibraryImageModal
          isOpen={isLibraryModalOpen}
          onClose={() => setIsLibraryModalOpen(false)}
          onImageSelected={handleLibrarySelection}
          projectId={projectId}
          title={t('image.selectReferenceTitle')}
          subtitle={t('image.selectReferenceSubtitle')}
          selectButtonText={t('image.useAsReference')}
          filterFunction={(item) => item.type === 'image'} // Only show images
        />
      )}
    </div>
  );
};

export default ReferenceImageUploader;
